import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Loader2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type DeliveryRow, getEndpoint, getStats, listDeliveries } from './api';

const PAGE_SIZE = 50;

export function PushDeliveriesPage({
  endpointId: endpointIdProp,
  onClose,
}: {
  endpointId?: number;
  onClose?: () => void;
} = {}) {
  // biome-ignore lint/suspicious/noExplicitAny: dynamic
  const params = useParams({ strict: false }) as any;
  const id = endpointIdProp ?? Number(params?.id);

  const [eventFilter, setEventFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'success' | 'failed' | 'pending' | ''>('');
  const [selected, setSelected] = useState<DeliveryRow | null>(null);

  const epQ = useQuery({
    queryKey: ['push-endpoint', id],
    queryFn: () => getEndpoint(id),
    enabled: Number.isFinite(id),
  });
  const statsQ = useQuery({
    queryKey: ['push-stats', id],
    queryFn: () => getStats(id),
    enabled: Number.isFinite(id),
  });
  const delQ = useInfiniteQuery({
    queryKey: ['push-deliveries', id, eventFilter, statusFilter],
    queryFn: ({ pageParam }) =>
      listDeliveries(id, {
        ...(eventFilter && { event_type: eventFilter }),
        ...(statusFilter && { status: statusFilter }),
        limit: PAGE_SIZE,
        ...(typeof pageParam === 'number' && { cursor: pageParam }),
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: Number.isFinite(id),
  });

  const deliveries = useMemo<DeliveryRow[]>(
    () => delQ.data?.pages.flatMap((p) => p.items) ?? [],
    [delQ.data],
  );

  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !delQ.hasNextPage || delQ.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) void delQ.fetchNextPage();
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delQ.hasNextPage, delQ.isFetchingNextPage, delQ.fetchNextPage]);

  // Mede primeira linha + header → maxHeight = 10 linhas exatas.
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [scrollMaxHeight, setScrollMaxHeight] = useState<number | null>(null);
  useEffect(() => {
    const t = tableRef.current;
    if (!t || deliveries.length === 0) return;
    const headerRow = t.querySelector('thead tr') as HTMLElement | null;
    const firstBodyRow = t.querySelector('tbody tr') as HTMLElement | null;
    if (!headerRow || !firstBodyRow) return;
    setScrollMaxHeight(headerRow.offsetHeight + firstBodyRow.offsetHeight * 10);
  }, [deliveries.length]);

  // ESC fecha modal.
  useEffect(() => {
    if (!onClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const stats = statsQ.data;

  return (
    <div className="flex flex-1 flex-col gap-4">
      <header className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="font-bold text-[20px] text-n-900" style={{ letterSpacing: '-.02em' }}>
            Entregas — {epQ.data?.name ?? '…'}
          </h1>
          <span className="font-mono text-[11px] text-n-500">{epQ.data?.url}</span>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded text-n-500 hover:bg-n-100 hover:text-n-700"
            title="Fechar"
          >
            <X size={18} />
          </button>
        )}
      </header>

      {/* Indicators */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          {(['24h', '7d', '30d'] as const).map((bucket) => {
            const s = stats[bucket];
            const rate = s && s.total > 0 ? Math.round((s.success / s.total) * 100) : 0;
            return (
              <div
                key={bucket}
                className="rounded-md border border-n-200 bg-n-0 p-4 shadow-sm"
              >
                <div className="text-[11px] font-bold uppercase tracking-[.04em] text-n-500">
                  Últimas {bucket}
                </div>
                <div className="mt-2 flex items-baseline gap-3">
                  <span className="font-bold text-[24px] text-n-900">{s?.total ?? 0}</span>
                  <span className="text-[11px] text-n-500">eventos</span>
                </div>
                <div className="mt-1 text-[12px]">
                  <span className="text-success-700">✓ {s?.success ?? 0}</span>
                  {' · '}
                  <span className="text-danger-700">✗ {s?.failed ?? 0}</span>
                  {' · '}
                  <span className="text-n-600">{rate}% sucesso</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-2">
        <select
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          className="h-9 rounded border border-n-200 bg-n-0 px-2 text-[12px]"
        >
          <option value="">Todos eventos</option>
          <option value="message.received">message.received</option>
          <option value="message.sent">message.sent</option>
          <option value="ticket.created">ticket.created</option>
          <option value="ticket.closed">ticket.closed</option>
          <option value="group.member_joined">group.member_joined</option>
          <option value="group.member_left">group.member_left</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'success' | 'failed' | 'pending' | '')}
          className="h-9 rounded border border-n-200 bg-n-0 px-2 text-[12px]"
        >
          <option value="">Todos status</option>
          <option value="success">Sucesso</option>
          <option value="failed">Falha</option>
          <option value="pending">Pendente</option>
        </select>
      </div>

      <div className="flex flex-1 gap-4 overflow-hidden">
        <div
          className="flex-1 overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm"
          style={scrollMaxHeight ? { maxHeight: scrollMaxHeight } : undefined}
        >
          <table ref={tableRef} className="w-full text-[13px]">
            <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
              <tr>
                <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                  Quando
                </th>
                <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                  Evento
                </th>
                <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                  HTTP
                </th>
                <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                  Tentativas
                </th>
                <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                  Resposta
                </th>
              </tr>
            </thead>
            <tbody>
              {delQ.isLoading && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center">
                    <Loader2 className="mx-auto animate-spin text-n-400" />
                  </td>
                </tr>
              )}
              {deliveries.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => setSelected(d)}
                  className={`cursor-pointer border-b border-n-100 hover:bg-n-50 ${
                    selected?.id === d.id ? 'bg-brand-50' : ''
                  }`}
                >
                  <td className="px-[14px] py-[11px] text-[11px] text-n-600">
                    {new Date(d.created_at).toLocaleString('pt-BR')}
                  </td>
                  <td className="px-[14px] py-[11px] text-[11px] text-n-700">
                    {d.event_type}
                  </td>
                  <td className="px-[14px] py-[11px]">
                    <span
                      className={`rounded px-2 py-0.5 font-mono text-[11px] ${
                        d.delivered_at
                          ? 'bg-success-50 text-success-700'
                          : d.failed_at
                            ? 'bg-danger-50 text-danger-700'
                            : 'bg-n-100 text-n-700'
                      }`}
                    >
                      {d.http_status ?? '—'}
                    </span>
                  </td>
                  <td className="px-[14px] py-[11px] text-n-700">{d.attempts}</td>
                  <td className="px-[14px] py-[11px] text-[11px] text-n-600">
                    {(d.response_body ?? '').slice(0, 60)}
                  </td>
                </tr>
              ))}
              {delQ.hasNextPage && (
                <tr ref={sentinelRef}>
                  <td colSpan={5} className="py-4 text-center text-[12px] text-n-500">
                    {delQ.isFetchingNextPage ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin" /> Carregando mais…
                      </span>
                    ) : (
                      'Role para carregar mais'
                    )}
                  </td>
                </tr>
              )}
              {!delQ.isLoading && deliveries.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-[12px] text-n-500">
                    Nenhuma entrega ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {selected && (
          <aside className="w-[400px] overflow-auto rounded-md border border-n-200 bg-n-0 p-4 shadow-sm">
            <h3 className="font-bold text-[14px] text-n-900">Delivery #{selected.id}</h3>
            <div className="mt-3 space-y-3 text-[12px]">
              <div>
                <span className="block font-semibold text-[11px] uppercase tracking-[.04em] text-n-500">
                  Payload
                </span>
                <pre className="mt-1 max-h-72 overflow-auto rounded bg-n-50 p-2 font-mono text-[11px]">
                  {JSON.stringify(selected.payload, null, 2)}
                </pre>
              </div>
              {selected.response_body && (
                <div>
                  <span className="block font-semibold text-[11px] uppercase tracking-[.04em] text-n-500">
                    Resposta
                  </span>
                  <pre className="mt-1 max-h-40 overflow-auto rounded bg-n-50 p-2 font-mono text-[11px]">
                    {selected.response_body}
                  </pre>
                </div>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
